import React, { useState, useEffect, useCallback } from 'react';
import { Users, Package } from 'lucide-react';
import SyncCard from '../components/Configuracion/SyncCard';
import supabase from '../config/supabase';
import { getNetlifyFunctionUrl, getLambdaUrl } from '../config/config';
import { useToast } from '../components/Toast/ToastContainer';
import './Configuracion.css';

const ConfiguracionIntegraciones = () => {
  const toast = useToast();
  
  const [syncEvents, setSyncEvents] = useState({
    'clientes-cl': null,
    'clientes-us': null,
    'inventario-us': null
  });
  
  const [executing, setExecuting] = useState({
    'clientes-cl': false,
    'clientes-us': false,
    'inventario-us': false
  });
  
  // Estado de ejecución para mostrar en el botón (persistente en localStorage)
  const [executionStates, setExecutionStates] = useState(() => {
    const saved = localStorage.getItem('sync_execution_states');
    return saved ? JSON.parse(saved) : {
      'clientes-cl': null,
      'clientes-us': null,
      'inventario-us': null
    };
  });
  
  /**
   * Obtiene el último evento de sincronización para un tipo específico
   */
  const fetchSyncEvent = useCallback(async (syncType) => {
    try {
      // Construir URL correcta según el ambiente (dev o prod)
      const functionUrl = getNetlifyFunctionUrl('sync-status');
      const url = `${functionUrl}?type=${syncType}&_t=${Date.now()}`;
      
      const response = await fetch(url, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.syncEvent) {
          // Convertir camelCase a snake_case para compatibilidad con SyncCard
          const syncEvent = {
            id: data.syncEvent.id,
            type: data.syncEvent.type,
            status: data.syncEvent.status,
            start_time: data.syncEvent.startTime,
            end_time: data.syncEvent.endTime,
            result: data.syncEvent.result,
            error: data.syncEvent.error,
            created_at: data.syncEvent.createdAt,
            updated_at: data.syncEvent.updatedAt
          };
          
          console.log(`Sync event actualizado para ${syncType}:`, syncEvent);
          
          setSyncEvents(prev => ({
            ...prev,
            [syncType]: syncEvent
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching sync event for ${syncType}:`, error);
    }
  }, []);
  
  /**
   * Obtiene todos los eventos de sincronización
   */
  const fetchAllSyncEvents = useCallback(async () => {
    await Promise.all([
      fetchSyncEvent('clientes-cl'),
      fetchSyncEvent('clientes-us'),
      fetchSyncEvent('inventario-us')
    ]);
  }, [fetchSyncEvent]);
  
  /**
   * Ejecuta la sincronización de clientes Chile mediante Lambda (síncrona)
   */
  const handleExecuteClientesCL = async () => {
    const syncType = 'clientes-cl';
    setExecuting(prev => ({ ...prev, [syncType]: true }));
    updateExecutionState(syncType, 'starting');
    
    try {
      const lambdaUrl = getLambdaUrl('defontana-clientes');
      
      if (!lambdaUrl) {
        throw new Error('URL de Lambda no configurada');
      }
      
      console.log(`Ejecutando Lambda Defontana Clientes: ${lambdaUrl}`);
      updateExecutionState(syncType, 'executing');
      
      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      updateExecutionState(syncType, 'processing');
      
      let data = {};
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      }
      
      if (response.ok) {
        console.log('Lambda ejecutada exitosamente:', data);
        
        // Limpiar el estado de ejecución inmediatamente después de recibir respuesta exitosa
        setExecuting(prev => ({ ...prev, [syncType]: false }));
        updateExecutionState(syncType, null);
        
        // Intentar actualizar múltiples veces para asegurar que se obtengan los datos
        const maxRetries = 8;
        let foundSuccess = false;
        
        for (let i = 0; i < maxRetries; i++) {
          console.log(`Intento ${i + 1}/${maxRetries} de actualizar sync_event...`);
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Forzar actualización
          await fetchSyncEvent(syncType);
          
          // Esperar un poco más para que React actualice el estado
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Verificar directamente desde la API
          try {
            const functionUrl = getNetlifyFunctionUrl('sync-status');
            const checkUrl = `${functionUrl}?type=${syncType}&_t=${Date.now()}`;
            const checkResponse = await fetch(checkUrl, { cache: 'no-cache' });
            
            if (checkResponse.ok) {
              const checkData = await checkResponse.json();
              if (checkData.success && checkData.syncEvent && checkData.syncEvent.status === 'success') {
                console.log('✓ Sincronización confirmada como exitosa');
                foundSuccess = true;
                break;
              }
            }
          } catch (checkError) {
            console.warn('Error verificando estado:', checkError);
          }
        }
        
        // Forzar una última actualización
        await fetchSyncEvent(syncType);
        
        toast.success(
          foundSuccess 
            ? 'Sincronización completada exitosamente.\n\nLos datos se han actualizado.'
            : 'Sincronización completada.\n\nLos datos se están actualizando...',
          6000
        );
      } else {
        throw new Error(data.error || data.message || `Error HTTP ${response.status}`);
      }
    } catch (error) {
      console.error(`Error executing Lambda for ${syncType}:`, error);
      toast.error(
        `Error al ejecutar sincronización:\n\n${error.message}`,
        8000
      );
    } finally {
      setExecuting(prev => ({ ...prev, [syncType]: false }));
      updateExecutionState(syncType, null);
    }
  };
  
  /**
   * Ejecuta la sincronización de clientes USA/Canada mediante Lambda (síncrona)
   */
  const handleExecuteClientesUS = async () => {
    const syncType = 'clientes-us';
    setExecuting(prev => ({ ...prev, [syncType]: true }));
    updateExecutionState(syncType, 'starting');
    
    try {
      const lambdaUrl = getLambdaUrl('quickbooks-clientes');
      
      if (!lambdaUrl) {
        throw new Error('URL de Lambda no configurada');
      }
      
      console.log(`Ejecutando Lambda QuickBooks Clientes: ${lambdaUrl}`);
      updateExecutionState(syncType, 'executing');
      
      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      updateExecutionState(syncType, 'processing');
      
      let data = {};
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      }
      
      if (response.ok) {
        console.log('Lambda ejecutada exitosamente:', data);
        
        // Limpiar el estado de ejecución inmediatamente después de recibir respuesta exitosa
        setExecuting(prev => ({ ...prev, [syncType]: false }));
        updateExecutionState(syncType, null);
        
        // Intentar actualizar múltiples veces para asegurar que se obtengan los datos
        const maxRetries = 8;
        let foundSuccess = false;
        
        for (let i = 0; i < maxRetries; i++) {
          console.log(`Intento ${i + 1}/${maxRetries} de actualizar sync_event...`);
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Forzar actualización
          await fetchSyncEvent(syncType);
          
          // Esperar un poco más para que React actualice el estado
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Verificar directamente desde la API
          try {
            const functionUrl = getNetlifyFunctionUrl('sync-status');
            const checkUrl = `${functionUrl}?type=${syncType}&_t=${Date.now()}`;
            const checkResponse = await fetch(checkUrl, { cache: 'no-cache' });
            
            if (checkResponse.ok) {
              const checkData = await checkResponse.json();
              if (checkData.success && checkData.syncEvent && checkData.syncEvent.status === 'success') {
                console.log('✓ Sincronización confirmada como exitosa');
                foundSuccess = true;
                break;
              }
            }
          } catch (checkError) {
            console.warn('Error verificando estado:', checkError);
          }
        }
        
        // Forzar una última actualización
        await fetchSyncEvent(syncType);
        
        toast.success(
          foundSuccess 
            ? 'Sincronización completada exitosamente.\n\nLos datos se han actualizado.'
            : 'Sincronización completada.\n\nLos datos se están actualizando...',
          6000
        );
      } else {
        throw new Error(data.error || data.message || `Error HTTP ${response.status}`);
      }
    } catch (error) {
      console.error(`Error executing Lambda for ${syncType}:`, error);
      toast.error(
        `Error al ejecutar sincronización:\n\n${error.message}`,
        8000
      );
    } finally {
      setExecuting(prev => ({ ...prev, [syncType]: false }));
      updateExecutionState(syncType, null);
    }
  };
  
  /**
   * Actualiza el estado de ejecución y lo persiste en localStorage
   */
  const updateExecutionState = (syncType, state) => {
    setExecutionStates(prev => {
      const newStates = { ...prev, [syncType]: state };
      localStorage.setItem('sync_execution_states', JSON.stringify(newStates));
      return newStates;
    });
  };
  
  /**
   * Ejecuta la sincronización de inventario USA mediante Lambda (síncrona)
   */
  const handleExecuteInventarioUS = async () => {
    const syncType = 'inventario-us';
    setExecuting(prev => ({ ...prev, [syncType]: true }));
    updateExecutionState(syncType, 'starting');
    
    try {
      const lambdaUrl = getLambdaUrl('kms-inventario');
      
      if (!lambdaUrl) {
        throw new Error('URL de Lambda no configurada');
      }
      
      console.log(`Ejecutando Lambda KMS Inventario: ${lambdaUrl}`);
      updateExecutionState(syncType, 'executing');
      
      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      updateExecutionState(syncType, 'processing');
      
      let data = {};
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      }
      
      if (response.ok) {
        console.log('Lambda ejecutada exitosamente:', data);
        
        // Limpiar el estado de ejecución inmediatamente después de recibir respuesta exitosa
        setExecuting(prev => ({ ...prev, [syncType]: false }));
        updateExecutionState(syncType, null);
        
        // Intentar actualizar múltiples veces para asegurar que se obtengan los datos
        const maxRetries = 8;
        let foundSuccess = false;
        
        for (let i = 0; i < maxRetries; i++) {
          console.log(`Intento ${i + 1}/${maxRetries} de actualizar sync_event...`);
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Forzar actualización
          await fetchSyncEvent(syncType);
          
          // Esperar un poco más para que React actualice el estado
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Verificar directamente desde la API
          try {
            const functionUrl = getNetlifyFunctionUrl('sync-status');
            const checkUrl = `${functionUrl}?type=${syncType}&_t=${Date.now()}`;
            const checkResponse = await fetch(checkUrl, { cache: 'no-cache' });
            
            if (checkResponse.ok) {
              const checkData = await checkResponse.json();
              if (checkData.success && checkData.syncEvent && checkData.syncEvent.status === 'success') {
                console.log('✓ Sincronización confirmada como exitosa');
                foundSuccess = true;
                break;
              }
            }
          } catch (checkError) {
            console.warn('Error verificando estado:', checkError);
          }
        }
        
        // Forzar una última actualización
        await fetchSyncEvent(syncType);
        
        toast.success(
          foundSuccess 
            ? 'Sincronización completada exitosamente.\n\nLos datos se han actualizado.'
            : 'Sincronización completada.\n\nLos datos se están actualizando...',
          6000
        );
      } else {
        throw new Error(data.error || data.message || `Error HTTP ${response.status}`);
      }
    } catch (error) {
      console.error(`Error executing Lambda for ${syncType}:`, error);
      toast.error(
        `Error al ejecutar sincronización:\n\n${error.message}`,
        8000
      );
    } finally {
      setExecuting(prev => ({ ...prev, [syncType]: false }));
      updateExecutionState(syncType, null);
    }
  };
  
  /**
   * Configurar polling para actualizar estados
   */
  useEffect(() => {
    // Limpiar estados de ejecución obsoletos del localStorage al montar el componente
    localStorage.removeItem('sync_execution_states');
    setExecutionStates({
      'clientes-cl': null,
      'clientes-us': null,
      'inventario-us': null
    });
    
    // Cargar datos iniciales
    fetchAllSyncEvents();
    
    // Configurar polling cada 10 segundos
    const interval = setInterval(() => {
      fetchAllSyncEvents();
    }, 10000);
    
    // Cleanup
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [fetchAllSyncEvents]);
  
  /**
   * Configurar suscripción a cambios en tiempo real de Supabase
   */
  useEffect(() => {
    // Suscribirse a cambios en la tabla sync_events
    const subscription = supabase
      .channel('sync_events_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'sync_events'
        },
        (payload) => {
          console.log('Cambio detectado en sync_events:', payload);
          
          // Actualizar el evento específico que cambió
          if (payload.new && payload.new.type) {
            const syncType = payload.new.type;
            
            // Actualizar el estado con los nuevos datos
            setSyncEvents(prev => ({
              ...prev,
              [syncType]: {
                id: payload.new.id,
                type: payload.new.type,
                status: payload.new.status,
                start_time: payload.new.start_time,
                end_time: payload.new.end_time,
                result: payload.new.result,
                error: payload.new.error,
                created_at: payload.new.created_at,
                updated_at: payload.new.updated_at
              }
            }));
          }
        }
      )
      .subscribe();
    
    // Cleanup
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  return (
    <div className="configuracion-container">
      <div className="configuracion-header">
        <h1 className="configuracion-title">Configuración - Integraciones</h1>
        <p className="configuracion-subtitle">
          Gestiona las sincronizaciones automáticas con sistemas externos
        </p>
      </div>
      
      <div className="configuracion-content">
        {/* Sección Clientes */}
        <section className="config-section">
          <div className="config-section-header">
            <Users size={24} className="config-section-icon" />
            <h2 className="config-section-title">Clientes</h2>
          </div>
          
          <div className="config-section-content">
            <SyncCard
              title="Clientes Chile"
              description="Sincronización con Defontana - Clientes de Chile"
              syncType="clientes-cl"
              syncEvent={syncEvents['clientes-cl']}
              onExecute={handleExecuteClientesCL}
              isExecuting={executing['clientes-cl']}
              executionState={executionStates['clientes-cl']}
            />
            
            <SyncCard
              title="Clientes USA y Canadá"
              description="Sincronización con QuickBooks - Clientes de USA y Canadá"
              syncType="clientes-us"
              syncEvent={syncEvents['clientes-us']}
              onExecute={handleExecuteClientesUS}
              isExecuting={executing['clientes-us']}
              executionState={executionStates['clientes-us']}
            />
          </div>
        </section>
        
        {/* Sección Inventario */}
        <section className="config-section">
          <div className="config-section-header">
            <Package size={24} className="config-section-icon" />
            <h2 className="config-section-title">Inventario</h2>
          </div>
          
          <div className="config-section-content">
            <SyncCard
              title="Inventario USA (KMS)"
              description="Sincronización con KMS - Inventario de Warehouse USA"
              syncType="inventario-us"
              syncEvent={syncEvents['inventario-us']}
              onExecute={handleExecuteInventarioUS}
              isExecuting={executing['inventario-us']}
              executionState={executionStates['inventario-us']}
            />
          </div>
        </section>
      </div>
      
      <div className="configuracion-footer">
        <p className="configuracion-footer-text">
          Las sincronizaciones se actualizan automáticamente en tiempo real
        </p>
      </div>
    </div>
  );
};

export default ConfiguracionIntegraciones;
